import { Close, Send, SmartToy } from '@mui/icons-material';
import { Avatar, Box, Button, Divider, IconButton, TextField, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useChatAgent } from '@src/App/Components/Agents/hooks/useChatAgent';
import { EAgentModule, type IAgentContext } from '@sharedInterfaces/ChatAgent';

interface OnboardingAgentProps {
    message: string;
    step: number;
    onClose: () => void;
    stepName?: string;
    onOpenMainChat?: () => void;
    employeeTalkId?: number;
}

const OnboardingAgent: React.FC<OnboardingAgentProps> = ({ message, step, onClose, stepName, onOpenMainChat, employeeTalkId }) => {
    const theme = useTheme();
    const [displayedMessage, setDisplayedMessage] = useState('');
    const [showInput, setShowInput] = useState(false);
    const [inputText, setInputText] = useState('');
    
    const { sendMessage, isTyping, messages } = useChatAgent({ dummyMessage: message });

    // Typewriter effect
    useEffect(() => {
        setDisplayedMessage('');
        let index = 0;
        const timer = setInterval(() => {
            if (index < message.length) {
                setDisplayedMessage(message.slice(0, index + 1));
                index++;
            } else {
                clearInterval(timer);
            }
        }, 30);

        return () => clearInterval(timer);
    }, [message]);

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        width: 40,
                        height: 40,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    }}
                >
                    <SmartToy />
                </Avatar>

                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            T4LO
                        </Typography>
                    </Box>

                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.text.primary,
                            lineHeight: 1.6,
                            minHeight: '1.5em',
                        }}
                    >
                        {displayedMessage}
                    </Typography>
                </Box>

                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                        },
                    }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: 2 }} />

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowInput(!showInput)}
                    sx={{
                        textTransform: 'none',
                        color: theme.palette.text.secondary,
                    }}
                >
                    Fragen?
                </Button>
            </Box>

            {/* Input Field */}
            {showInput && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Stellen Sie Ihre Frage..."
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter' && !isTyping && inputText.trim()) {
                                const agentContext: IAgentContext = {
                                    module: EAgentModule.ONBOARDING,
                                    subContext: {
                                        employeeTalkId,
                                        currentStep: step,
                                        stepName: stepName || `Schritt ${step + 1}`
                                    }
                                };
                                await sendMessage(inputText, agentContext);
                                setInputText('');
                                setShowInput(false);
                                onOpenMainChat?.();
                            }
                        }}
                    />
                    <IconButton
                        size="small"
                        disabled={isTyping || !inputText.trim()}
                        onClick={async () => {
                            const agentContext: IAgentContext = {
                                module: EAgentModule.ONBOARDING,
                                subContext: {
                                    employeeTalkId: employeeTalkId,
                                    currentStep: step,
                                    stepName: stepName || `Schritt ${step + 1}`
                                }
                            };
                            await sendMessage(inputText, agentContext);
                            setInputText('');
                            setShowInput(false);
                            onOpenMainChat?.();
                        }}
                    >
                        <Send fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
};

export default OnboardingAgent;
